import { AsyncHelper } from '@bryntum/schedulerpro';
import { useCallback, useEffect, useState } from 'react';
import { ResourceManagmentApi } from 'api/resource-management';
import { toastService } from 'components/molecules/toast';
import { GroupedFilter } from 'components/organisms/tasks/grouped-filter-popover';
import { FilterGroupType } from 'components/pages/types';
import { useTranslation } from 'hooks/use-translation';
import { queueMacrotask } from 'utils/macrotask';
import { useLoadingRenderer } from '../scheduler-config/loading-renderer';
import { ViewPresetId } from '../scheduler-config/view-presets/constants';
import {
  AvailabilityFilter,
  ResourceManagmentPageParams,
  SchedulerReference,
} from '../types';

interface Props {
  schedulerRef: SchedulerReference;
  isSchedulerLoadedWithData: boolean;
  pageParams: ResourceManagmentPageParams;
  setPageParams: (nextParams: ResourceManagmentPageParams) => void;
  isHoursPerWeek: boolean;
}

interface ReturnProps {
  availableResources: number[] | undefined;
  currentAvailabilityFilter: AvailabilityFilter | undefined;
  availabilityFilterChangeHandler: (props: {
    availabilityFilter?: any;
    filter: GroupedFilter;
  }) => Promise<void>;
}

export const useAvailabilityFilter = ({
  schedulerRef,
  isSchedulerLoadedWithData,
  pageParams,
  setPageParams,
  isHoursPerWeek,
}: Props): ReturnProps => {
  const { showLoading, hideLoading } = useLoadingRenderer({ schedulerRef });

  const [availableResources, setAvailableResources] =
    useState<number[] | undefined>();
  const [isAvailabilityFilterApiLoading, setIsAvailabilityFilterApiLoading] =
    useState(false);
  const [currentAvailabilityFilter, setCurrentAvailabilityFilter] =
    useState<AvailabilityFilter | undefined>(undefined);

  const errorTitle = useTranslation('failure');
  const errorMessage = useTranslation('error');

  const availabilityFilterHandler = useCallback(
    async (availabilityFilter?: AvailabilityFilter) => {
      if (availabilityFilter == null) {
        return undefined;
      }
      showLoading();
      await AsyncHelper.sleep(10);

      const { roles: _, ...rest } = availabilityFilter;

      try {
        const resources = await ResourceManagmentApi.findAvailableResources({
          ...rest,
          isHoursPerWeek,
        });

        hideLoading();
        setAvailableResources(resources);

        return resources;
      } catch (e) {
        toastService({
          title: errorTitle,
          message: errorMessage,
        }).error();

        hideLoading();
        setAvailableResources(undefined);

        return undefined;
      }
    },
    [errorMessage, errorTitle, hideLoading, showLoading, isHoursPerWeek],
  );

  const availabilityFilterChangeHandler = useCallback(
    async ({
      availabilityFilter,
      filter,
    }: {
      availabilityFilter?: AvailabilityFilter;
      filter: GroupedFilter;
    }) => {
      const newUserGroupCriteria = filter.groupCriteria.filter(
        ({ id }) => id === FilterGroupType.User,
      );

      const existingProjectGroupCriteria =
        pageParams.filter.groupCriteria.filter(
          ({ id }) => id === FilterGroupType.Project,
        );

      setPageParams({
        ...pageParams,
        availabilityFilter,
        filter: {
          ...filter,
          groupCriteria: [
            ...existingProjectGroupCriteria,
            ...newUserGroupCriteria,
          ],
        },
      });
      if (availabilityFilter == null) {
        setCurrentAvailabilityFilter(undefined);
      }
    },
    [pageParams, setPageParams],
  );

  useEffect(() => {
    /**
     * Availability filter needs to applied for the following cases:
     * 1. setting manually from the drawer
     * 2. on load if the filter was already set
     * Hence, making the API call in useEffect to account for both these cases
     */

    // TODO: this was calling the API twice in dev; to check in prod
    if (
      isSchedulerLoadedWithData &&
      !isAvailabilityFilterApiLoading &&
      pageParams.availabilityFilter != null
    ) {
      const isSameAvailabilityFilter =
        (pageParams.availabilityFilter.startDate.isSame(
          currentAvailabilityFilter?.startDate,
          'day',
        ) &&
          pageParams.availabilityFilter.endDate.isSame(
            currentAvailabilityFilter?.endDate,
            'day',
          ) &&
          pageParams.availabilityFilter.availableMinutes ===
            currentAvailabilityFilter?.availableMinutes) ??
        false;

      if (!isSameAvailabilityFilter) {
        setIsAvailabilityFilterApiLoading(true); // this is to make sure API is not called multiple times

        availabilityFilterHandler(pageParams.availabilityFilter).then(
          (resources) => {
            setPageParams({
              filter: pageParams.filter,
              availabilityFilter:
                resources == null ? undefined : pageParams.availabilityFilter,
              viewPreset:
                resources == null ? pageParams.viewPreset : ViewPresetId.Week,
            });
            setCurrentAvailabilityFilter(
              resources == null ? undefined : pageParams.availabilityFilter,
            );

            setIsAvailabilityFilterApiLoading(false);
            queueMacrotask(() => {
              schedulerRef.current.instance.trigger('availabilityFilterChange');
            });

            if (
              resources != null &&
              pageParams.viewPreset !== ViewPresetId.Week
            ) {
              schedulerRef.current.instance.suspendRefresh();
              schedulerRef.current.instance.eventStore.removeAll();
              schedulerRef.current.instance.resumeRefresh(true);
              schedulerRef.current.instance.trigger('onViewPresetChange');
            }
          },
        );
      }
    }
  }, [
    availabilityFilterHandler,
    currentAvailabilityFilter,
    isAvailabilityFilterApiLoading,
    isSchedulerLoadedWithData,
    pageParams.availabilityFilter,
    pageParams.filter,
    pageParams.viewPreset,
    schedulerRef,
    setPageParams,
  ]);

  return {
    availableResources,
    currentAvailabilityFilter,
    availabilityFilterChangeHandler,
  };
};
